import { ReactElement, useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { Grid, H } from '@farewill/ui'

import CrematoriumPicker from 'components/form/crematorium-picker'
import useApi from 'lib/effects/api'
import { Crematorium as CrematoriumModel } from 'lib/models/crematorium'
import { FuneralPlanAttributes } from 'lib/models/funeral-plan'
import { CaseDetailsSectionProps } from './types'

type CrematoriumResource = {
  id: number
  attributes: {
    name: string
    address: string
  }
}

export type CrematoriumProps = CaseDetailsSectionProps & {
  customerPostcode?: string | null
}

const Crematorium = ({
  handleSave,
  customerPostcode,
}: CrematoriumProps): ReactElement => {
  const { values } = useFormikContext<FuneralPlanAttributes>()
  const [{ data: crematoria }, fetchCrematoria] =
    useApi<CrematoriumResource[]>()
  const [crematorium, setCrematorium] = useState<CrematoriumModel>()

  useEffect(() => {
    fetchCrematoria({ url: '/api/crematoria' })
  }, [fetchCrematoria])

  /** The plan only stores the id, so the name and address the picker shows are
   * resolved from the crematoria list once it arrives. */
  useEffect(() => {
    const selected = crematoria?.find(({ id }) => id === values.crematoriumId)

    if (selected) {
      setCrematorium({
        id: selected.id,
        name: selected.attributes.name,
        address: selected.attributes.address,
      })
    }
  }, [crematoria, values.crematoriumId])

  /** Show the change straight away, but put back what is actually saved if the
   * save fails - the failure itself is reported by the global error banner. */
  const saveCrematorium = async (updated?: CrematoriumModel) => {
    const previous = crematorium
    setCrematorium(updated)

    const saved = await handleSave({
      name: 'crematoriumId',
      value: updated?.id ?? null,
    })

    if (!saved) setCrematorium(previous)
  }

  return (
    <>
      <H size="S">Crematorium</H>
      <Grid data-testid="choose-a-crematorium">
        <CrematoriumPicker
          crematorium={crematorium}
          customerPostcode={customerPostcode}
          boldHeader={false}
          onReset={() => saveCrematorium(undefined)}
          onCrematoriumChanged={saveCrematorium}
        />
      </Grid>
    </>
  )
}

export default Crematorium
